import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, TextField, Tooltip, Typography, Popper, Paper, CircularProgress } from '@mui/material';

import HighlightIcon from '@mui/icons-material/Highlight';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export function reportMatches(doc, query) {
  if (!query.trim()) return [];
  let text = '';
  const positions = [];
  doc.descendants((node, pos) => {
    if (node.isText) {
      for (let i = 0; i < node.text.length; i++) { text += node.text[i]; positions.push(pos + i); }
    } else if (node.isBlock && text) { text += '\n'; positions.push(pos); }
  });
  const needle = query.trim().toLowerCase();
  const haystack = text.toLowerCase();
  const matches = [];
  let start = 0;
  while ((start = haystack.indexOf(needle, start)) !== -1) {
    matches.push({ from: positions[start], to: positions[start + needle.length - 1] + 1 });
    start += needle.length;
  }
  return matches;
}
const hint = 'Select text in the report or type a phrase. The count shows matches in this report. Click Check sources to find supporting passages in the source files.';
export default function ReportSourceSearch({ editor, onCheck, checking }) {
  const [query, setQuery] = useState('');
  const [count, setCount] = useState(0);
  const [popup, setPopup] = useState(null);
  const programmatic = useRef(false);
  const queryRef = useRef('');
  queryRef.current = query;
  const highlightKey = useRef(new PluginKey('reportSourceSearch'));
  useEffect(() => {
    if (!editor?.registerPlugin) return undefined;
    const key = highlightKey.current;
    editor.registerPlugin(new Plugin({
      key,
      state: {
        init: () => DecorationSet.empty,
        apply: (transaction, previous) => {
          const match = transaction.getMeta(key);
          if (match !== undefined) return match ? DecorationSet.create(transaction.doc, [Decoration.inline(match.from, match.to, {
            style: 'background-color: #BBD6FB; color: inherit;',
            'data-source-search-match': 'true',
          })]) : DecorationSet.empty;
          return previous.map(transaction.mapping, transaction.doc);
        },
      },
      props: { decorations: state => key.getState(state) },
    }));
    return () => { if (!editor.isDestroyed) editor.unregisterPlugin(key); };
  }, [editor]);
  const markMatch = match => {
    if (editor?.view?.dispatch) editor.view.dispatch(editor.state.tr.setMeta(highlightKey.current, match || null));
  };
  useEffect(() => {
    const dom = editor?.view?.dom;
    if (!dom) return undefined;
    const show = () => {
      const selection = window.getSelection();
      if (!selection?.rangeCount || selection.isCollapsed || !dom.contains(selection.anchorNode) || !dom.contains(selection.focusNode)) { setPopup(null); return; }
      const text = selection.toString().trim();
      if (!text) return;
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      setQuery(text); setCount(reportMatches(editor.state.doc, text).length);
      setPopup({ text, anchor: { getBoundingClientRect: () => rect } });
    };
    const dismiss = event => { if (!event.target.closest('[data-source-search-popup]')) setPopup(null); };
    dom.addEventListener('mouseup', show);
    dom.addEventListener('keyup', show);
    document.addEventListener('mousedown', dismiss);
    return () => { dom.removeEventListener('mouseup', show); dom.removeEventListener('keyup', show); document.removeEventListener('mousedown', dismiss); };
  }, [editor]);
  useEffect(() => {
    setQuery(''); setCount(0); setPopup(null);
    if (!editor) return undefined;
    const selected = () => {
      if (programmatic.current) return;
      markMatch(null);
      const { from, to } = editor.state.selection;
      if (from === to) return;
      const text = editor.state.doc.textBetween(from, to, '\n');
      setQuery(text); setCount(reportMatches(editor.state.doc, text).length);
    };
    const updated = () => {
      const matches = reportMatches(editor.state.doc, queryRef.current);
      setCount(matches.length);
      markMatch(matches[0]);
      setPopup(null);
    };
    editor.on('selectionUpdate', selected);
    editor.on('update', updated);
    return () => { editor.off('selectionUpdate', selected); editor.off('update', updated); };
  }, [editor]);
  return <><Popper open={Boolean(popup)} anchorEl={popup?.anchor} placement="top" sx={{ zIndex: 1500 }}>
    <Paper data-source-search-popup elevation={0} sx={{
      position: 'relative', mb: 1, bgcolor: '#1C1C2E', borderRadius: '8px', px: 1.5, py: .75,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: .5,
      '&::after': { content: '""', position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', borderWidth: '6px 6px 0', borderStyle: 'solid', borderColor: '#1C1C2E transparent transparent' },
    }}>
      <Button size="small" disabled={checking} onMouseDown={event => event.preventDefault()} onClick={() => { onCheck(popup.text); setPopup(null); }}
        startIcon={checking ? <CircularProgress size={12} color="inherit" /> : <HighlightIcon sx={{ fontSize: 14 }} />}
        sx={{ color: '#FFFFFF', textTransform: 'none', fontSize: 12, fontWeight: 600, minWidth: 0, p: 0, '&:hover': { bgcolor: 'transparent', opacity: .8 } }}>
        {checking ? 'Finding Sources...' : 'Check Sources'}
      </Button>
    </Paper>
  </Popper><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, flexWrap: 'wrap' }}>
    <Tooltip title={hint} describeChild><TextField size="small" label="Check sources" value={query}
      sx={{ flex: 1, minWidth: 160 }} onChange={event => {
        const value = event.target.value;
        const matches = editor ? reportMatches(editor.state.doc, value) : [];
        setPopup(null);
        programmatic.current = true;
        markMatch(matches[0]);
        // Set selection without focusing the editor, so typing can continue here.
        if (matches.length) {
          editor.commands.setTextSelection(matches[0]);
          editor.commands.scrollIntoView?.();
        } else if (editor) {
          editor.commands.setTextSelection(editor.state.selection.from);
        }
        programmatic.current = false;
        setQuery(value); setCount(matches.length);
      }} /></Tooltip>
    <Typography aria-live="polite" sx={{ fontSize: 12, color: 'text.secondary' }}>{count} {count === 1 ? 'match' : 'matches'}</Typography>
    <Tooltip title={hint}><span><Button variant="outlined" disabled={!query.trim() || checking || !editor} onClick={() => onCheck(query.trim())} sx={{ textTransform: 'none' }}>{checking ? 'Checking…' : 'Check sources'}</Button></span></Tooltip>
  </Box></>;
}
